// src/api/client.js
import axios from 'axios';
import { supabase } from '../supabaseClient';
import { isTokenValid } from '@lib/auth';
import useAuthStore from '@stores/authStore';
import useUiStore from '@stores/uiStore';
import i18n from 'i18next';

const PUBLIC_PATHS = ['/order', '/venuesordner', '/kiosk', '/discover', '/modules', '/reserve'];

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

function getApiBasePath() {
  const rawBase = import.meta.env.VITE_API_BASE_URL;
  if (!rawBase) return '';
  try {
    // Works for both absolute and relative base URLs.
    const parsed = new URL(rawBase, window.location.origin);
    return parsed.pathname.replace(/\/+$/, '');
  } catch {
    return String(rawBase).replace(/\/+$/, '');
  }
}

function normalizeRequestUrl(config) {
  if (!config || typeof config.url !== 'string') return config;
  if (/^https?:\/\//i.test(config.url)) return config;

  const basePath = getApiBasePath();
  if (!basePath || basePath === '/') return config;

  const duplicatePrefix = `${basePath}/`;
  if (config.url === basePath) {
    config.url = '/';
    return config;
  }
  if (config.url.startsWith(duplicatePrefix)) {
    config.url = config.url.slice(basePath.length);
  }
  return config;
}

// ---------------------------------------------------------------------------
// Token refresh: share a single in-flight promise across concurrent requests
// ---------------------------------------------------------------------------
let refreshPromise = null;

async function refreshTokens(currentToken) {
  const storedRefreshToken = useAuthStore.getState().refreshToken;

  // Only attempt backend refresh when we actually have a refresh token
  if (storedRefreshToken) {
    try {
      const refreshResponse = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
        { refreshToken: storedRefreshToken },
        { headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {} },
      );
      const newToken =
        refreshResponse.data?.session?.access_token ??
        refreshResponse.data?.access_token;
      const newRefreshToken =
        refreshResponse.data?.session?.refresh_token ?? null;

      if (newToken) {
        useAuthStore.getState().setToken(newToken);
        if (newRefreshToken) {
          useAuthStore.setState({ refreshToken: newRefreshToken });
        }
        return newToken;
      }
    } catch {
      // Backend refresh failed — fall through to Supabase
    }
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      useAuthStore.getState().setToken(session.access_token);
      return session.access_token;
    }
  } catch {
    // Supabase session unavailable
  }

  return null;
}

// ---------------------------------------------------------------------------
// Request interceptor: attach a valid JWT token
// ---------------------------------------------------------------------------
api.interceptors.request.use(async (config) => {
  normalizeRequestUrl(config);

  const path = window.location.pathname.toLowerCase();
  const isPublicRoute = PUBLIC_PATHS.some((p) => path.startsWith(p));

  let token = useAuthStore.getState().token;

  // Only attempt refresh on non-public routes
  if (!isPublicRoute && !isTokenValid(token)) {
    if (!refreshPromise) {
      refreshPromise = refreshTokens(token).finally(() => { refreshPromise = null; });
    }
    const newToken = await refreshPromise;
    if (newToken) token = newToken;
  }

  // Still attach token if valid (e.g. logged-in staff browsing /order)
  if (isTokenValid(token)) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor: clear backend errors on success, handle failures
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => {
    const { backendStatus, clearBackendError } = useUiStore.getState();
    if (backendStatus !== 'connected') {
      clearBackendError();
    }
    return response;
  },
  (error) => {
    if (error.response) {
      // Server responded -> backend is reachable
      const status = error.response.status;

      if (status === 401 || status === 403) {
        const token = useAuthStore.getState().token;

        if (!isTokenValid(token)) {
          useAuthStore.getState().logout();
          supabase.auth.signOut();

          const path = window.location.pathname.toLowerCase();
          const isPublic = PUBLIC_PATHS.some((p) => path.startsWith(p));

          if (!isPublic && path !== '/login') {
            window.location.replace('/login');
          }
        }
      }
    } else if (error.request) {
      // No response received -> backend unreachable
      useUiStore.getState().setBackendStatus(
        'unreachable',
        i18n.t('common:connection.lost', 'Verbindung zum Server verloren. Bitte später erneut versuchen.'),
      );
    }

    return Promise.reject(error);
  },
);

export default api;
